import React, { useState, useEffect } from 'react';
import { Alert, Card, Button, ButtonGroup } from '@cimpress/react-components';
import { Toast } from './Toast';
import { ImportByText } from './ImportByText';
import { ImportByTemplate } from './ImportByTemplate';
import { useParams } from 'react-router-dom';
import { getTranslations } from '../api/get-translations';
import { getLocalizationRetranslationData } from '../api/get-localization-retranslation-data';
import { getMissingCultures } from '../api/get-missing-cultures';
import config from '../config';
import { formatBatchDate } from '../libs/formatBatchDate';

const getBatchHeading = (batchId) => {
  let folderPath;
  try {
    folderPath = decodeURIComponent(batchId);
  } catch {
    // Router history can already have decoded a literal percent sign.
    folderPath = batchId.replace(/%2f/gi, '/');
  }
  return formatBatchDate(folderPath);
};

export const ImportPageS3 = () => {
  const [message, setMessage] = useState({ message: '', severity: 'info' });
  let { accountId, areaId, batchId } = useParams();
  const [translations, setTranslations] = useState([]);
  const [counts, setCounts] = useState({ textsCount: 0, templatesCount: 0 });
  const [retranslations, setRetranslations] = useState([]);

  const [mode, setMode] = useState('byTemplate');
  const [missingCultures, setMissingCultures] = useState([]);

  const setToast = (messages, severity) => {
    var d = new Date();
    var version = d.toString();
    setMessage({ messages, severity, version });
  };

  const cardHeader = <div>{getBatchHeading(batchId)}</div>;

  useEffect(() => {
    const tenant = { accountId, areaId };
    batchId &&
      getMissingCultures(tenant, batchId)
        .then(setMissingCultures)
        .catch((err) => console.log('Error while checking missing cultures', err));
  }, [batchId, accountId, areaId]);

  useEffect(() => {
    const tenant = { accountId, areaId };
    batchId &&
      getTranslations(tenant, batchId).then((res) => {
        const localizations = Object.entries(res).map(([key, value]) => ({
          culture: key,
          items: value,
        }));

        const items = localizations.flatMap((l) =>
          Object.keys(l.items).map((key) => ({
            adtifUrl: l.items[key].adtif,
            localizationKey: key,
          })),
        );
        const distinctLocalizationKeys = [...new Set(items.map((x) => x.localizationKey))];
        const retranslationPromises = distinctLocalizationKeys.map((localizationKey) =>
          getLocalizationRetranslationData(localizationKey, { accountId, areaId }),
        );
        const derivedCultures = config.mockAccountsConfiguration[accountId]?.area[areaId]?.derivedCultures ?? {};

        const groupRetranslationByLocalizationId = (reTranslationItems) => {
          const retranslationMap = reTranslationItems.reduce((a, item) => {
            if (item && Object.keys(item).length > 0) {
              const culture = Object.keys(item)[0];
              const localizationId = item[culture].localizationId;
              const derivedItemsMap = Object.keys(item)
                .flatMap((itemKey) => {
                  if (itemKey in derivedCultures) {
                    const culturesToDerive = derivedCultures[itemKey];
                    const derivedItems = culturesToDerive.map((cultureToDerive) => {
                      return { ...item[itemKey], culture: cultureToDerive };
                    });
                    return derivedItems;
                  }
                })
                .filter((x) => x);
              derivedItemsMap.map((x) => (item[x.culture] = x));
              a[localizationId] = item;
            }
            return a;
          }, {});
          setRetranslations(retranslationMap);
        };
        Promise.all(retranslationPromises).then((reTranslationItems) =>
          groupRetranslationByLocalizationId(reTranslationItems),
        );
        // setRetranslations(Promise.all(retranslationPromises))
        const distinctTemplates = [...new Set(items.map((x) => x.adtifUrl))];

        setCounts({ textsCount: distinctLocalizationKeys.length, templatesCount: distinctTemplates.length });
        setTranslations(res);
      });
  }, [batchId, accountId, areaId]);

  return (
    <>
      <div className="container-fluid designs-page">
        <div className="row">
          <div className="col-sm-12 col-md-10 col-md-offset-1">
            <Card header={cardHeader}>
              {missingCultures.length > 0 && (
                <Alert
                  type="warning"
                  title="Incomplete batch"
                  message={`The following locales are not yet in this batch: ${missingCultures.join(
                    ', ',
                  )}. Importing now will leave those locales untranslated.`}
                  dismissible={false}
                />
              )}
              <ButtonGroup>
                <Button type={mode === 'byTemplate' ? 'primary' : 'default'} onClick={() => setMode('byTemplate')}>
                  Templates ({counts.templatesCount})
                </Button>
                <Button type={mode === 'byText' ? 'primary' : 'default'} onClick={() => setMode('byText')}>
                  Texts ({counts.textsCount})
                </Button>
              </ButtonGroup>
              {mode === 'byText' && (
                <ImportByText setToast={setToast} translations={translations} retranslation={retranslations} />
              )}
              {mode === 'byTemplate' && (
                <ImportByTemplate setToast={setToast} translations={translations} retranslation={retranslations} />
              )}
              <Toast messages={message.messages} severity={message.severity} version={message.version} />
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};
